import { Color, Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { DashboardPageLink } from 'dashboard/views/DashboardSandboxHomeView/constants';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';

interface ChooseIntegrationMethodProps {
  isStepCompleted: boolean;
  isSavingCompletedStep: boolean;
  saveCompletedStep: () => void;
}

enum IntegrationMethod {
  HostedFlow = 'Hosted Flow',
  EmbeddedFlow = 'Embedded Flow',
  MobileSdks = 'Mobile SDKs',
  ServerApis = 'Server APIs',
}

const ChooseIntegrationMethod = ({
  isStepCompleted,
  isSavingCompletedStep,
  saveCompletedStep,
}: ChooseIntegrationMethodProps) => {
  const [selectedIntegrationMethod, setSelectedIntegrationMethod] =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    useState<IntegrationMethod>(null);
  const integrationMethodToDashboardPageLinkMap = {
    [IntegrationMethod.HostedFlow]: DashboardPageLink.IntegrationMethodHostedFlow,
    [IntegrationMethod.EmbeddedFlow]: DashboardPageLink.IntegrationMethodEmbeddedFlow,
    [IntegrationMethod.MobileSdks]: DashboardPageLink.IntegrationMethodMobileSdks,
    [IntegrationMethod.ServerApis]: DashboardPageLink.IntegrationMethodServerApis,
  };

  return (
    <>
      <Paragraph color='primary'>
        You’ve set up your solution on Persona. Now we’ll integrate this solution into your product.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Where in your product do you want your users to find your Persona flow? Is it on your
        website, in a mobile app, or both? Persona supports all of these options and more.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        To get started, choose one of the options below. These options are called integration
        methods. Note that you can use multiple methods together.
      </Paragraph>
      <Spacer height={2} />
      <IntegrationOption
        isSelected={selectedIntegrationMethod === IntegrationMethod.HostedFlow}
        onClick={() => setSelectedIntegrationMethod(IntegrationMethod.HostedFlow)}
      >
        <Paragraph color='primary'>
          <b>Hosted Flow</b>: The fastest way to integrate. Can be set up with no coding. For each
          user, you create and share a unique link to your Persona flow, and the link is hosted by
          Persona. A user can open the link in any browser.
        </Paragraph>
      </IntegrationOption>
      <IntegrationOption
        isSelected={selectedIntegrationMethod === IntegrationMethod.EmbeddedFlow}
        onClick={() => setSelectedIntegrationMethod(IntegrationMethod.EmbeddedFlow)}
      >
        <Paragraph color='primary'>
          <b>Embedded Flow</b>: Embed the Persona flow on your website. Your users complete the flow
          directly on your website. Persona provides a code snippet that can be added to your
          website with low engineering effort.
        </Paragraph>
      </IntegrationOption>
      <IntegrationOption
        isSelected={selectedIntegrationMethod === IntegrationMethod.MobileSdks}
        onClick={() => setSelectedIntegrationMethod(IntegrationMethod.MobileSdks)}
      >
        <Paragraph color='primary'>
          <b>Mobile SDKs</b>: Add the Persona flow into your native mobile application. Persona
          offers SDKs for both iOS and Android.
        </Paragraph>
      </IntegrationOption>
      <IntegrationOption
        isSelected={selectedIntegrationMethod === IntegrationMethod.ServerApis}
        onClick={() => setSelectedIntegrationMethod(IntegrationMethod.ServerApis)}
      >
        <Paragraph color='primary'>
          <b>Server APIs</b>: Recommended only for very custom needs. Requires the largest
          engineering effort. You build the collection flow experience for your users, and pass that
          information to Persona through APIs.
        </Paragraph>
      </IntegrationOption>
      {selectedIntegrationMethod && (
        <>
          {isSavingCompletedStep ? (
            <Button disabled width='button'>
              Saving...
            </Button>
          ) : (
            <Button
              id='home-sandbox-onboarding-checklist__view-integration-guide'
              width='auto'
              onClick={() => {
                if (!isStepCompleted) {
                  saveCompletedStep();
                }
                window.open(integrationMethodToDashboardPageLinkMap[selectedIntegrationMethod]);
              }}
            >
              View {selectedIntegrationMethod} integration guide
            </Button>
          )}
        </>
      )}
    </>
  );
};

const IntegrationOption = styled('div')<{ isSelected: boolean }>`
  padding: ${Spacing[2]} ${Spacing[4]};
  margin-bottom: ${Spacing[3]};
  border: 1px solid ${Color.CoreSlate400};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  cursor: pointer;
  transition: ${TransitionDuration.Medium};

  :hover {
    background-color: ${Color.CoreSlate100};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${Color.CoreSlate100};
    `}
`;

export default ChooseIntegrationMethod;
